import React, { createContext, useContext, useState, useEffect } from "react";
import { get_user_type, logout as apiLogout } from "../api/AuthApi";
import { Spin } from "antd";

const AUTH_USER_TYPE_KEY = "user_type";
const AUTH_CHANNEL_KEY = "channel";

const isJwtToken = (token) =>
  typeof token === "string" && token.split(".").length === 3;

const AuthContext = createContext(null);

export const useAuth = () => {
  return useContext(AuthContext);
};

const buildUserFromSession = (token) => {
  const userType = sessionStorage.getItem(AUTH_USER_TYPE_KEY);
  if (!userType) {
    return null;
  }

  return {
    token,
    user_type: userType,
    channel: sessionStorage.getItem(AUTH_CHANNEL_KEY) || null,
    z_user_id: sessionStorage.getItem("z_user_id") || null,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenExpiry, setTokenExpiry] = useState(null);

  const getTokenExpiry = (token) => {
    if (!token || !isJwtToken(token)) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.exp) {
        return payload.exp * 1000;
      }
    } catch (error) {
      console.error("Error parsing token:", error);
    }
    return null;
  };

  const persistSession = (userData) => {
    sessionStorage.setItem("authToken", userData.access_token);
    if (userData.z_user_id) {
      sessionStorage.setItem("z_user_id", userData.z_user_id);
    }
    if (userData.user_type) {
      sessionStorage.setItem(AUTH_USER_TYPE_KEY, userData.user_type);
    }
    if (userData.channel) {
      sessionStorage.setItem(AUTH_CHANNEL_KEY, userData.channel);
    }
  };

  const clearSession = () => {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("z_user_id");
    sessionStorage.removeItem(AUTH_USER_TYPE_KEY);
    sessionStorage.removeItem(AUTH_CHANNEL_KEY);
    setTokenExpiry(null);
    setUser(null);
  };

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = sessionStorage.getItem("authToken");

      if (!token) {
        setLoading(false);
        return;
      }

      setTokenExpiry(getTokenExpiry(token));

      const cachedUser = buildUserFromSession(token);
      if (cachedUser) {
        setUser(cachedUser);
      }

      try {
        const userData = await get_user_type();

        sessionStorage.setItem(AUTH_USER_TYPE_KEY, userData.user_type);
        if (userData.channel) {
          sessionStorage.setItem(AUTH_CHANNEL_KEY, userData.channel);
        }

        setUser({
          token,
          user_type: userData.user_type,
          channel: userData.channel,
          z_user_id: sessionStorage.getItem("z_user_id"),
        });
      } catch (error) {
        console.error("Failed to get user data:", error);

        if (!cachedUser) {
          clearSession();
        }
      }

      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = (userData) => {
    persistSession(userData);
    setTokenExpiry(getTokenExpiry(userData.access_token));

    setUser({
      ...userData,
      token: userData.access_token,
      channel: userData.channel,
    });
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      clearSession();
    }
  };

  const hasRole = (roles) => {
    if (!user) return false;

    const userRole = user.user_type;

    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }

    return userRole === roles;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <Spin spinning={true} fullscreen /> : children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
